// NEXUS Owner AI Builder — Emergent-style white prompt, tutorial, model + JSON toggle.
// No NX cost. Uses Claude Sonnet 4.6 (default), Sonnet 4.5, or Opus 4.7 via the Emergent LLM key.
import { useEffect, useRef, useState } from "react";
import { Send, Bot, User as UserIcon, Loader2, BookOpen, Braces, ChevronDown, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { renderSafeMarkdown } from "../../lib/safeMarkdown";

const MODELS = [
  { id: "sonnet-4-5", label: "Claude Sonnet 4.5", desc: "Fast · code-tuned · verified on Emergent key" },
];

const STARTER_PROMPTS = [
  "Add a 'Boost analytics' widget to CreatorHQ that shows last-7-day impressions per post.",
  "Draft a new /api/owner/audit endpoint that dumps the last 500 wallet transactions as CSV.",
  "How would I add face-tracking AR filters to the video-call view without breaking the LiveKit room?",
  "Refactor server.py — pull the auth routes into /app/backend/routes/auth.py.",
];

export default function OwnerBuilderTab() {
  const [thread, setThread] = useState([
    { who: "bot", text: "**Hey Kaye.** I'm the NEXUS Builder — I know the codebase, database, and every module you've shipped. Ask me anything about the platform and I'll draft the code + paths. Tick **JSON** if you want structured output you can paste into automation.", md: true },
  ]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sid, setSid] = useState(null);
  const [model, setModel] = useState("sonnet-4-5");
  const [jsonMode, setJsonMode] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [thread]);

  const send = async (override) => {
    const q = (override ?? draft).trim();
    if (!q || sending) return;
    setThread((t) => [...t, { who: "me", text: q }]);
    setDraft("");
    setSending(true);
    try {
      const { data } = await api.post("/owner/builder/chat", { session_id: sid, message: q, model, format: jsonMode ? "json" : "text" });
      setSid(data.session_id);
      setThread((t) => [...t, { who: "bot", text: data.reply, md: !jsonMode, json: jsonMode }]);
    } catch (e) {
      setThread((t) => [...t, { who: "bot", text: "(error talking to the builder) " + (e?.response?.data?.detail || "") }]);
    }
    setSending(false);
  };

  return (
    <div className="space-y-3 owner-builder-light" data-testid="owner-builder-tab">
      <style>{`
        .owner-builder-light .b-card { background:#ffffff; color:#0b0b0b; border:1px solid #e5e7eb; }
        .owner-builder-light .b-input { background:#ffffff; color:#0b0b0b; border:1px solid #d4d4d8; }
        .owner-builder-light .b-input::placeholder { color:#71717a; }
        .owner-builder-light .b-msg-bot { background:#f4f4f5; color:#0b0b0b; border:1px solid #e5e7eb; }
        .owner-builder-light .b-msg-me  { background:#0b0b0b; color:#ffffff; border:1px solid #0b0b0b; }
        .owner-builder-light .b-btn     { background:#0b0b0b; color:#fff; border-radius:9999px; padding:8px 16px; font-size:12px; letter-spacing:0.2em; text-transform:uppercase; }
        .owner-builder-light .b-btn:disabled { opacity:.4; }
        .owner-builder-light .b-chip    { background:#f4f4f5; color:#0b0b0b; border:1px solid #d4d4d8; padding:4px 10px; border-radius:9999px; font-size:11px; }
        .owner-builder-light .b-chip.active { background:#0b0b0b; color:#fff; }
        .owner-builder-light .b-code    { background:#0b0b0b; color:#a1f0c8; }
        .owner-builder-light pre, .owner-builder-light code { color: inherit; }
        .owner-builder-light .prose-black * { color:#0b0b0b !important; }
      `}</style>

      <div className="flex items-center gap-2 flex-wrap">
        <Bot className="w-4 h-4 text-nexus-cyan" />
        <div className="label-eyebrow text-nexus-cyan">// nexus builder · free · claude sonnet 4.5</div>
        <button
          onClick={() => setShowTutorial((v) => !v)}
          data-testid="builder-tutorial-toggle"
          className="b-chip inline-flex items-center gap-1 hover:brightness-95"
        ><BookOpen className="w-3 h-3" /> Tutorial</button>
      </div>

      {showTutorial && (
        <div className="b-card p-4 rounded-2xl text-sm leading-relaxed" data-testid="builder-tutorial">
          <div className="font-display font-bold text-base mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-nexus-cyan" /> How to use the Builder</div>
          <ol className="list-decimal list-inside space-y-1 text-[13px]">
            <li>Describe what you want — a feature, refactor, bug fix, or platform question.</li>
            <li>Sonnet 4.5 is fast + free and runs on the Emergent Universal LLM key.</li>
            <li>Tick <b>JSON</b> when you want machine-readable output (paths + snippets + caveats).</li>
            <li>The Builder plans the change — it does NOT run it. Copy the paths + code into the deploy chat to actually ship.</li>
            <li>Every session keeps context. Follow-up messages remember your prior asks.</li>
          </ol>
          <div className="mt-3 text-[11px] text-black/60">Costs nothing. Uses the Emergent Universal LLM key. Logs are stored under <code>db.owner_builder_log</code> for your audit trail.</div>
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-widest text-black/50 mb-1">Starter prompts</div>
            <div className="flex flex-wrap gap-1.5">
              {STARTER_PROMPTS.map((p, i) => (
                <button key={i} onClick={() => setDraft(p)} className="b-chip hover:brightness-95" data-testid={`starter-prompt-${i}`}>{p.slice(0, 42)}…</button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        {MODELS.map((m) => (
          <button
            key={m.id}
            onClick={() => setModel(m.id)}
            data-testid={`builder-model-${m.id}`}
            className={`b-chip ${model === m.id ? "active" : ""}`}
            title={m.desc}
          >{m.label}</button>
        ))}
        <button
          onClick={() => setJsonMode((v) => !v)}
          data-testid="builder-json-toggle"
          className={`b-chip inline-flex items-center gap-1 ${jsonMode ? "active" : ""}`}
          title="Emit strict JSON: {summary, files[], snippets[], caveats[]}"
        ><Braces className="w-3 h-3" /> JSON</button>
      </div>

      <div ref={scrollRef} className="b-card h-[500px] overflow-y-auto p-4 space-y-3 rounded-2xl" data-testid="builder-thread">
        {thread.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.who === "me" ? "justify-end" : ""}`} data-testid={`builder-msg-${m.who}-${i}`}>
            {m.who === "bot" && <Bot className="w-4 h-4 text-nexus-cyan shrink-0 mt-1.5" />}
            <div className={`max-w-[85%] text-sm p-3 rounded-2xl ${m.who === "me" ? "b-msg-me" : "b-msg-bot"}`}>
              {m.json ? (
                <pre className="b-code rounded-lg p-3 text-[11px] leading-snug overflow-x-auto whitespace-pre-wrap">{m.text}</pre>
              ) : m.md ? (
                <div className="prose-black" dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(m.text) }} />
              ) : (
                <div className="whitespace-pre-wrap">{m.text}</div>
              )}
            </div>
            {m.who === "me" && <UserIcon className="w-4 h-4 text-white/60 shrink-0 mt-1.5" />}
          </div>
        ))}
        {sending && (
          <div className="flex gap-2 items-center text-black/50 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> drafting…</div>
        )}
      </div>

      <div className="b-card rounded-2xl p-3 flex flex-col gap-2" data-testid="builder-input-card">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
          rows={3}
          placeholder={jsonMode ? "Ask for a structured plan — I'll reply with JSON. (Ctrl/⌘+Enter to send)" : "Tell me what to change — I'll draft the code and file paths. (Ctrl/⌘+Enter to send)"}
          className="b-input w-full px-3 py-2 text-sm font-mono rounded-xl focus:outline-none focus:ring-2 focus:ring-nexus-cyan/30"
          data-testid="builder-input"
        />
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-black/50 uppercase tracking-widest">Free · no NX cost · {MODELS.find((m) => m.id === model)?.label} {jsonMode ? "· JSON" : ""}</div>
          <button onClick={() => send()} disabled={sending || !draft.trim()} className="b-btn inline-flex items-center gap-1.5" data-testid="builder-send">
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}
